import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { invalidateCompanyQueries } from "@/lib/companyQueries";
import { listPathFor } from "./recordFormat";

/**
 * DELETE /api/companies/:id is a soft archive (archived_at) that the Archived
 * page can restore, so the copy says archive. invalidateCompanyQueries carries
 * the real list keys; the old page's own list missed "/api/prospects" and
 * "/api/capital-providers", so those lists kept showing archived rows.
 */
export function ArchiveCompanyDialog({ company, open, onOpenChange }: {
  company: { id: string; name: string; category: string }; open: boolean; onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const [busy, setBusy] = useState(false);

  const archive = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/companies/${company.id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Couldn't archive the company");
      void invalidateCompanyQueries(qc, company.id);
      toast.success(`Archived ${company.name}`);
      onOpenChange(false);
      setLocation(listPathFor(company.category));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't archive the company");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(o) => { if (!busy) onOpenChange(o); }}
      title={`Archive ${company.name}?`}
      description="It leaves every list and search. People, activity and documents stay attached, and it can be restored from the Archived page."
      confirmLabel="Archive"
      cancelLabel="Cancel"
      variant="destructive"
      isLoading={busy}
      onConfirm={archive}
    />
  );
}
